import { PageController } from '@client/components/_core/layout/PageController';
import { VAddressInput } from '@client/components/_shared/forms/view-form/VAddressInput';
import { VDropdown } from '@client/components/_shared/forms/view-form/VDropdown';
import { VEntityOwnerSelect } from '@client/components/_shared/forms/view-form/VEntityOwnerSelect';
import { VReadOnly } from '@client/components/_shared/forms/view-form/VReadOnly';
import { VTextInput } from '@client/components/_shared/forms/view-form/VTextInput';
import { CardTitle } from '@client/components/_ui/display/CardTitle';
import { DetailItem, DetailList } from '@client/components/_ui/display/DetailList';
import { confirmAction } from '@client/components/_ui/feedback/modal';
import { RepairAuthorisationsCard } from '@client/components/claims/authorisations/RepairAuthorisationsCard';
import { ClaimDecisionBadge } from '@client/components/claims/ClaimDecisionBadge';
import { AiTriageSectionCard } from '@client/components/claims/unified-assessment/shared';
import { VehicleDowntimeCard } from '@client/components/claims/vehicle-downtime/VehicleDowntimeCard';
import { TaskList } from '@client/components/tasks/TaskList';
import { useConsoleAddressSuggestions } from '@client/hooks/api/useConsoleAddressSuggestions';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useConfigService } from '@client/hooks/business/configuration/useConfigService';
import { useOrg } from '@client/hooks/business/org/useOrg';
import { useUser } from '@client/hooks/business/user/useUser';
import { useDeferredSectionsReady } from '@client/hooks/system/useDeferredSectionsReady';
import { useFeatureFlag } from '@client/hooks/useFeatureFlag';
import { ICONS } from '@client/styles/theme';
import { canReadAnyComplaint } from '@shared/permissions/complaint';
import { hasPermission } from '@shared/permissions/roles';
import { createFileRoute, Outlet } from '@tanstack/react-router';
import { Card, Col, Row, Space } from 'antd';
import { lazy, type ReactNode, Suspense } from 'react';
import { claimFormStore } from '../store';
import { getClaimEditorAccess } from './claim-editor-access';

const DeferredLinkedClaims = lazy(() =>
    import('@client/components/claims/LinkedClaims').then((module) => ({ default: module.LinkedClaims })),
);
const DeferredComplaints = lazy(() =>
    import('@client/components/complaints/Complaints').then((module) => ({ default: module.Complaints })),
);
const DeferredManageStrategy = lazy(() =>
    import('./ManageStrategy').then((module) => ({ default: module.ManageStrategy })),
);
const DeferredManageTimeline = lazy(() =>
    import('./ManageTimeline').then((module) => ({ default: module.ClaimManageTimeline })),
);
const DeferredSupplierAndJobs = lazy(() =>
    import('./SupplierAndJobs').then((module) => ({ default: module.SupplierAndJobs })),
);

export const Route = createFileRoute('/console/$orgId/claims/details/$id/manage')({
    component: RouteComponent,
});

function RouteComponent() {
    return (
        <PageController route={Route}>
            <ManageClaim />
        </PageController>
    );
}

function DeferredSectionTitle({ icon, label }: { icon: ReactNode; label: string }) {
    return (
        <div className="flex items-center gap-2">
            {icon}
            <span>{label}</span>
        </div>
    );
}

function DeferredManageSectionsFallback({ showComplaints }: { showComplaints: boolean }) {
    return (
        <Space orientation="vertical" size="middle" className="w-full">
            <Row gutter={16}>
                <Col span={12}>
                    <Card
                        loading
                        size="small"
                        className="h-[220px]"
                        title={
                            <DeferredSectionTitle
                                icon={<ICONS.outcome className="card-title-icon" />}
                                label="Strategy"
                            />
                        }
                    />
                </Col>
                <Col span={12}>
                    <Card
                        loading
                        size="small"
                        className="h-[220px]"
                        title={
                            showComplaints ? (
                                <DeferredSectionTitle
                                    icon={<ICONS.complaint className="card-title-icon" />}
                                    label="Complaints"
                                />
                            ) : (
                                <DeferredSectionTitle
                                    icon={<ICONS.claim className="card-title-icon" />}
                                    label="Linked claims"
                                />
                            )
                        }
                    />
                </Col>
            </Row>
            <Row gutter={16}>
                <Col span={12}>
                    <Card
                        loading
                        size="small"
                        className="h-[260px]"
                        title={
                            <DeferredSectionTitle
                                icon={<ICONS.supplier className="card-title-icon" />}
                                label="Supplier and jobs"
                            />
                        }
                    />
                </Col>
                {showComplaints && (
                    <Col span={12}>
                        <Card
                            loading
                            size="small"
                            className="h-[260px]"
                            title={
                                <DeferredSectionTitle
                                    icon={<ICONS.claim className="card-title-icon" />}
                                    label="Linked claims"
                                />
                            }
                        />
                    </Col>
                )}
            </Row>
            <Row>
                <Col span={24}>
                    <Card
                        loading
                        size="small"
                        className="h-[190px]"
                        title={
                            <DeferredSectionTitle
                                icon={<ICONS.timeline className="card-title-icon" />}
                                label="Timeline"
                            />
                        }
                    />
                </Col>
            </Row>
        </Space>
    );
}

function DeferredManageSections({
    showComplaints,
    isReadOnly,
    allowComplaintAssociation,
    claimId,
}: {
    showComplaints: boolean;
    isReadOnly: boolean;
    allowComplaintAssociation: boolean;
    claimId: bigint;
}) {
    return (
        <Space orientation="vertical" size="middle" className="w-full">
            <Row gutter={16}>
                <Col span={12}>
                    <DeferredManageStrategy />
                </Col>
                <Col span={12}>
                    {showComplaints ? (
                        <DeferredComplaints
                            claimId={claimId}
                            isReadOnly={isReadOnly}
                            allowAssociation={allowComplaintAssociation}
                        />
                    ) : (
                        <DeferredLinkedClaims claimId={claimId} isReadOnly={isReadOnly} />
                    )}
                </Col>
            </Row>
            <Row gutter={16}>
                <Col span={12}>
                    <DeferredSupplierAndJobs />
                </Col>
                {showComplaints && (
                    <Col span={12}>
                        <DeferredLinkedClaims claimId={claimId} isReadOnly={isReadOnly} />
                    </Col>
                )}
            </Row>
            <Row>
                <Col span={24}>
                    <DeferredManageTimeline />
                </Col>
            </Row>
        </Space>
    );
}

function ManageClaim() {
    const user = useUser();
    const claim = claimFormStore.use.formData();
    const isReadOnly = claimFormStore.use.isReadonly();
    const configService = useConfigService();
    const { trpcUtils } = useTrpc();
    const hasCompliance = useOrg().enabledModules.includes('compliance');
    // The complaints card is server-filtered by the caller's complaint read scope, so for a user
    // holding no complaint read permission it would only ever render empty — with a live "Add
    // complaint" button opening an equally empty picker. Give that slot to linked claims instead.
    // Note this is deliberately not the gate for the legacy free-text Complaint# field below, which
    // tracks the org's compliance module rather than the user's permissions.
    const showComplaints = hasCompliance && canReadAnyComplaint(user.roles);
    const addressSuggestion = useConsoleAddressSuggestions();
    const deferredSectionsReady = useDeferredSectionsReady();

    const enableAiTriage = useFeatureFlag('enable-ai-triage', false);
    const isOwner = claim.ownerId === user.id;
    const canEditStatus =
        hasPermission(user.roles, 'claim:update') || (isOwner && hasPermission(user.roles, 'claim:update:is_owner'));
    const claimEditorAccess = getClaimEditorAccess(user, claim, isReadOnly);

    const VEntityOwnerSelectInstance = VEntityOwnerSelect({
        entity: 'claim',
        formStore: claimFormStore,
        isReadOnly: claimEditorAccess.isReadOnly,
        async onBeforeSave(values, currentData) {
            if (values.ownerId === currentData.ownerId) {
                return true;
            }

            return confirmAction({
                title: 'Confirm reassignment',
                content: (
                    <>
                        All open tasks currently assigned to the existing claim owner, as well as any unassigned open
                        tasks within the claim, will be reassigned to the new owner.
                    </>
                ),
                okText: 'Proceed',
                cancelText: 'Cancel',
            });
        },
        onAfterSave() {
            trpcUtils.task.listTasks.invalidate();
        },
    });

    return (
        <>
            <Outlet />
            <Space orientation="vertical" size="middle" className="w-full">
                {/* AI Triage Section - Early assessment from FNOL data */}
                {enableAiTriage && (
                    <Row gutter={16}>
                        <Col span={24}>
                            <AiTriageSectionCard claimId={claim.id} />
                        </Col>
                    </Row>
                )}
                {/* Fleet claims only (CUR-1052); renders nothing until a repairer quote has been evaluated. */}
                <RepairAuthorisationsCard claimId={claim.id} productCategory={claim.ProductType?.category} />
                <VehicleDowntimeCard claim={claim} />
                <Row gutter={[16, 16]}>
                    <Col xs={24} xl={12}>
                        <Card
                            title={<CardTitle icon={<ICONS.claimSnapshot />} title="Claim snapshot" />}
                            size="small"
                            className="h-full"
                        >
                            <DetailList columns={3}>
                                <DetailItem>
                                    <VReadOnly
                                        label="Claim#"
                                        field="generatedId"
                                        formStore={claimFormStore}
                                        formatValueFn={(value, { id }) => value || id}
                                    />
                                </DetailItem>
                                <DetailItem>
                                    <VEntityOwnerSelectInstance.HandlingParty />
                                </DetailItem>
                                <DetailItem>
                                    <VEntityOwnerSelectInstance.Owner />
                                </DetailItem>
                                <DetailItem>
                                    <VDropdown
                                        label="Claim Status"
                                        field="statusId"
                                        formStore={claimFormStore}
                                        isReadOnly={!claimEditorAccess.hasUpdateAccess || !canEditStatus}
                                        loaderFn={async () => {
                                            const { rows } =
                                                await trpcUtils.claimStatus.listClaimStatusesForSelect.fetch();

                                            return rows.map((item) => ({
                                                value: item.id,
                                                label: item.name,
                                            }));
                                        }}
                                    />
                                </DetailItem>
                                <DetailItem>
                                    <VDropdown
                                        label="Decision"
                                        field="decision"
                                        formStore={claimFormStore}
                                        formatValueFn={(value) =>
                                            typeof value === 'string' && value.trim() ? (
                                                <ClaimDecisionBadge decision={value} />
                                            ) : (
                                                '-'
                                            )
                                        }
                                        isReadOnly={claimEditorAccess.isReadOnly || !isOwner}
                                        loaderFn={async () => {
                                            const data = await configService.fetchClaimDecisions();

                                            return data.map((item) => ({
                                                value: item,
                                            }));
                                        }}
                                    />
                                </DetailItem>
                                <DetailItem>
                                    <VDropdown
                                        label="Triage outcome"
                                        field="triageOutcome"
                                        formStore={claimFormStore}
                                        isReadOnly={claimEditorAccess.isReadOnly || !isOwner}
                                        loaderFn={async () => {
                                            const data = await configService.fetchClaimTriageOutcome();

                                            return data.map((item) => ({
                                                value: item,
                                            }));
                                        }}
                                    />
                                </DetailItem>
                                <DetailItem>
                                    <VTextInput
                                        label="Cause of Loss"
                                        field="causeOfLoss"
                                        formStore={claimFormStore}
                                        isReadOnly={claimEditorAccess.isReadOnly}
                                    />
                                </DetailItem>
                                <DetailItem>
                                    <VAddressInput
                                        label="Address of loss"
                                        formStore={claimFormStore}
                                        isReadOnly={claimEditorAccess.isReadOnly}
                                        fields={{
                                            line1: 'addressLine1OfLoss',
                                            line2: 'addressLine2OfLoss',
                                            suburb: 'suburbOfLoss',
                                            state: 'stateOfLoss',
                                            postcode: 'postcodeOfLoss',
                                        }}
                                        {...addressSuggestion}
                                    />
                                </DetailItem>
                                <DetailItem>
                                    <VTextInput
                                        label="Life Cycle"
                                        field="lifecycle"
                                        formStore={claimFormStore}
                                        isReadOnly
                                    />
                                </DetailItem>
                                <DetailItem>
                                    <VTextInput
                                        label="External#"
                                        field="externalId"
                                        formStore={claimFormStore}
                                        isReadOnly={claimEditorAccess.isReadOnly}
                                    />
                                </DetailItem>
                                {!hasCompliance && (
                                    <DetailItem>
                                        <VTextInput
                                            label="Complaint#"
                                            field="externalComplaintNo"
                                            formStore={claimFormStore}
                                            isReadOnly={claimEditorAccess.isReadOnly}
                                        />
                                    </DetailItem>
                                )}
                                <DetailItem>
                                    <VDropdown
                                        label="Catastrophe event"
                                        field="catastropheEventId"
                                        formStore={claimFormStore}
                                        isReadOnly={claimEditorAccess.isReadOnly}
                                        allowClear
                                        showSearch
                                        formatValueFn={(_value, item) => item.CatastropheEvent?.name || '-'}
                                        loaderFn={async () => {
                                            if (!claim?.claimOccurredAt) {
                                                return [];
                                            }

                                            const data = await trpcUtils.catastropheEvent.listCatastropheEvents.fetch({
                                                where: {
                                                    startDate: {
                                                        lte: claim.claimOccurredAt,
                                                    },
                                                    endDate: {
                                                        gte: claim.claimOccurredAt,
                                                    },
                                                },
                                                orderBy: { name: 'asc' },
                                            });

                                            return data.rows.map(({ id, name }) => ({
                                                label: name,
                                                value: id,
                                            }));
                                        }}
                                    />
                                </DetailItem>
                            </DetailList>
                        </Card>
                    </Col>
                    <Col xs={24} xl={12}>
                        <TaskList
                            size="small"
                            relatedEntity="claim"
                            relatedEntityId={claim.id}
                            hideAddTask={isReadOnly}
                            handlingPartyIds={claim.HandlingParties.map(({ id }) => id)}
                        />
                    </Col>
                </Row>
                {deferredSectionsReady ? (
                    <Suspense fallback={<DeferredManageSectionsFallback showComplaints={showComplaints} />}>
                        <DeferredManageSections
                            showComplaints={showComplaints}
                            isReadOnly={isReadOnly}
                            allowComplaintAssociation={claimEditorAccess.hasUpdateAccess}
                            claimId={claim.id}
                        />
                    </Suspense>
                ) : (
                    <DeferredManageSectionsFallback showComplaints={showComplaints} />
                )}
            </Space>
        </>
    );
}
